import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemText, 
  IconButton, 
  useMediaQuery, 
  useTheme,
  Container
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { title: 'Home', path: '/' },
    { title: 'About', path: '/about' },
    { title: 'Technology', path: '/technology' },
    { title: 'Transparency', path: '/transparency' },
    { title: 'Developers', path: '/developers' },
    { title: 'FAQ', path: '/faq' },
  ];

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) return;
    setIsOpen(open);
  };

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{
          background: scrolled 
            ? 'rgba(0, 0, 0, 0.95)' 
            : 'rgba(0, 0, 0, 0.85)',
          backdropFilter: scrolled ? 'blur(20px)' : 'blur(10px)',
          WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'blur(10px)',
          borderBottom: scrolled ? '1px solid rgba(212, 175, 55, 0.3)' : '1px solid rgba(212, 175, 55, 0.15)',
          transition: 'all 0.4s ease',
          py: scrolled ? 0.5 : 1,
          boxShadow: scrolled ? '0 10px 40px rgba(0, 0, 0, 0.6)' : 'none',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: 'auto' }}>
            
            {/* Logo */}
            <Box 
              component={RouterLink} 
              to="/" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                textDecoration: 'none',
                '& img': { 
                  height: scrolled ? '45px' : '60px',
                  transition: 'height 0.4s ease',
                  objectFit: 'contain',
                }
              }}
            >
              <img src="/images/logo.png" alt="Diamond Dollar Logo" />
            </Box>

            {/* Desktop Menu */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3.5 }}>
                {navLinks.map((link) => (
                  <Typography
                    key={link.title}
                    component={RouterLink}
                    to={link.path}
                    sx={{
                      textDecoration: 'none',
                      color: location.pathname === link.path ? '#d4af37' : '#a1a1a1', 
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      letterSpacing: '1.5px',
                      textTransform: 'uppercase',
                      position: 'relative',
                      transition: 'color 0.3s ease',
                      '&:hover': { color: '#ffffff' },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '-8px',
                        left: 0,
                        width: location.pathname === link.path ? '100%' : '0',
                        height: '2px',
                        backgroundColor: '#d4af37',
                        boxShadow: '0 0 10px rgba(212, 175, 55, 0.5)',
                        transition: 'width 0.3s ease',
                      },
                      '&:hover::after': { width: '100%' },
                    }}
                  >
                    {link.title}
                  </Typography>
                ))}

                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(135deg, #d4af37 0%, #b8962f 100%)',
                    color: '#000000',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    letterSpacing: '1.5px',
                    textTransform: 'uppercase',
                    padding: '10px 24px',
                    borderRadius: '4px',
                    boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
                    transition: 'all 0.4s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #e8c84a 0%, #d4af37 100%)',
                      boxShadow: '0 8px 25px rgba(212, 175, 55, 0.5)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Get Started
                </Button>
              </Box>
            )}

            {/* Mobile Menu Icon */}
            {isMobile && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={toggleDrawer(true)}
                sx={{ 
                  color: '#ffffff', 
                  '&:hover': { color: '#d4af37' },
                  '& .MuiSvgIcon-root': { fontSize: '30px' }
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            background: 'rgba(0, 0, 0, 0.98)',
            width: '80%',
            maxWidth: '380px',
            borderLeft: '1px solid rgba(212, 175, 55, 0.3)',
            boxShadow: '-10px 0 40px rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
          <IconButton onClick={toggleDrawer(false)} sx={{ color: '#ffffff', '&:hover': { color: '#d4af37' } }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4, mt: 2 }}>
          <img src="/images/logo.png" alt="Diamond Dollar Logo" style={{ height: '60px', objectFit: 'contain' }} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box sx={{ 
            width: '60px', 
            height: '2px', 
            background: 'linear-gradient(90deg, transparent, #d4af37, transparent)',
            borderRadius: '2px',
          }} />
        </Box>

        <List sx={{ padding: '0 24px' }}>
          {navLinks.map((link) => (
            <ListItem key={link.title} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton 
                component={RouterLink} 
                to={link.path}
                sx={{
                  borderRadius: '4px',
                  padding: '14px 16px',
                  border: location.pathname === link.path ? '1px solid rgba(212, 175, 55, 0.4)' : '1px solid transparent',
                  backgroundColor: location.pathname === link.path ? 'rgba(212, 175, 55, 0.1)' : 'transparent',
                  '&:hover': { 
                    backgroundColor: 'rgba(212, 175, 55, 0.15)', 
                    borderColor: 'rgba(212, 175, 55, 0.5)'
                  },
                }}
              >
                <ListItemText 
                  primary={link.title}
                  primaryTypographyProps={{
                    color: location.pathname === link.path ? '#d4af37' : '#ffffff',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    letterSpacing: '1.5px',
                    textTransform: 'uppercase',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          
          <ListItem disablePadding sx={{ mt: 3 }}>
            <Button
              component={RouterLink}
              to="/contact"
              variant="contained"
              fullWidth
              sx={{
                background: 'linear-gradient(135deg, #d4af37 0%, #b8962f 100%)',
                color: '#000000',
                fontWeight: 700,
                letterSpacing: '1.5px',
                textTransform: 'uppercase',
                padding: '14px',
                borderRadius: '4px',
                boxShadow: '0 4px 15px rgba(212, 175, 55, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #e8c84a 0%, #d4af37 100%)',
                  boxShadow: '0 8px 25px rgba(212, 175, 55, 0.5)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Get Started
            </Button>
          </ListItem>
        </List>
      </Drawer>
    </>
  );
}

export default Navbar;